import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { EnvelopeSimple, ChatCircleText, Bell } from '@phosphor-icons/react'
import { endOfDay, startOfDay, subDays } from 'date-fns'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import API from '../../../../api/api'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { DEFAULT_TZ } from '@/constants/date'
import Filter from '../../../Filter/Filter'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'

import Tooltip from './Tooltip'
import AnalyticsCard from './AnalyticsCard'
import AnnouncmentStaticticsLoader from './Loader'
import ActivateSmsCard from './ActivateSmsCard'

const getTooltipTexts = (t) => ({
  Email: {
    subscribed: t('organisation:communicationPortal.analytics.tooltipTexts.email.subscribed'),
    currentSubscribed: t(
      'organisation:communicationPortal.analytics.tooltipTexts.email.currentSubscribed',
    ),
    futureSubscribers: t(
      'organisation:communicationPortal.analytics.tooltipTexts.email.futureSubscribers',
    ),
    pastSubscribed: t(
      'organisation:communicationPortal.analytics.tooltipTexts.email.pastSubscribed',
    ),
    deliveryRate: t('organisation:communicationPortal.analytics.tooltipTexts.email.deliveryRate'),
    openRate: t('organisation:communicationPortal.analytics.tooltipTexts.email.openRate'),
    clickRate: t('organisation:communicationPortal.analytics.tooltipTexts.email.clickRate'),
  },
  'Push notifications': {
    subscribed: t(
      'organisation:communicationPortal.analytics.tooltipTexts.pushNotifications.subscribed',
    ),
    currentSubscribed: t(
      'organisation:communicationPortal.analytics.tooltipTexts.pushNotifications.currentSubscribed',
    ),
    futureSubscribers: t(
      'organisation:communicationPortal.analytics.tooltipTexts.pushNotifications.futureSubscribers',
    ),
    pastSubscribed: t(
      'organisation:communicationPortal.analytics.tooltipTexts.pushNotifications.pastSubscribed',
    ),
  },
  SMS: {
    subscribed: t('organisation:communicationPortal.analytics.tooltipTexts.sms.subscribed'),
    currentSubscribed: t(
      'organisation:communicationPortal.analytics.tooltipTexts.sms.currentSubscribed',
    ),
    futureSubscribers: t(
      'organisation:communicationPortal.analytics.tooltipTexts.sms.futureSubscribers',
    ),
    pastSubscribed: t('organisation:communicationPortal.analytics.tooltipTexts.sms.pastSubscribed'),
  },
})

const AnnouncementsAnalytics = ({
  isOpen = true,
  mailingListOptions,
  selectedBuilding,
  messageTypeCount,
  shouldShowActivateSmsPanel,
}) => {
  const { t } = useTranslation(['organisation'])
  const TOOLTIP_TEXTS = getTooltipTexts(t)
  const { displaySnackbar } = useSnackbar()

  const preparedOptions = useMemo(
    () =>
      mailingListOptions.length
        ? mailingListOptions.map((option) => ({ ...option, text: option.label }))
        : mailingListOptions,
    [mailingListOptions],
  )
  const [analytics, setAnalytics] = useState({})
  const [mailingListFilters, setMailingListFilters] = useState([])
  const [period, setPeriod] = useState([])
  const [isLoading, setLoading] = useState(false)

  const fetchBuildingAnalytics = async (id, mailingList, from, to) => {
    try {
      setLoading(true)
      setAnalytics({})
      const { data } = await API.getBuildingCommunicationAnalytics(id, mailingList, from, to)
      setAnalytics(data)
    } catch {
      displaySnackbar(t('organisation:communicationPortal.analytics.toasts.loadError'), true)
    } finally {
      setLoading(false)
    }
  }

  const buildingTimezone = selectedBuilding?.timezone ?? DEFAULT_TZ
  // Note: Wall-clock "now" in the building's timezone (the browser's own timezone is irrelevant)
  const buildingToday = useMemo(() => toZonedTime(new Date(), buildingTimezone), [buildingTimezone])

  useEffect(() => {
    if (selectedBuilding && !period.length) {
      setPeriod([startOfDay(subDays(buildingToday, 7)), buildingToday])
    }
  }, [selectedBuilding])

  useEffect(() => {
    if (preparedOptions.length && !mailingListFilters.length) {
      setMailingListFilters([preparedOptions[0]])
    }
  }, [preparedOptions])

  useEffect(() => {
    const isMailingListReady = mailingListFilters.length > 0 || preparedOptions.length === 0

    if (isOpen && period[0] && period[1] && selectedBuilding && isMailingListReady) {
      const { id } = selectedBuilding

      const timeStart = fromZonedTime(startOfDay(period[0]), buildingTimezone).getTime()
      const timeEnd = fromZonedTime(endOfDay(period[1]), buildingTimezone).getTime()
      const filterId = mailingListFilters[0] ? mailingListFilters[0].id : ''

      fetchBuildingAnalytics(id, filterId, timeStart, timeEnd)
    }
  }, [
    isOpen,
    period,
    mailingListFilters,
    selectedBuilding,
    messageTypeCount,
    preparedOptions.length,
  ])

  return (
    <div className="ctp-communication-portal__analytics">
      <AnnouncmentStaticticsLoader isLoading={isLoading} />
      <div className="columns  is-multiline">
        <div className="column is-three-quarters header-column">
          <p>{t('organisation:communicationPortal.analytics.selectMailingList')}</p>
          <Tooltip
            tooltips={t('organisation:communicationPortal.analytics.tooltips', {
              returnObjects: true,
            })}
          />

          <Filter
            placeholder={
              mailingListFilters.length
                ? mailingListFilters[0].text
                : t('organisation:communicationPortal.analytics.selectOptionPlaceholder')
            }
            options={preparedOptions}
            value={mailingListFilters}
            name={'MailingListFilter'}
            additionalClass="filter"
            closeAfterSelect
            onChange={(e) => setMailingListFilters([e.target.value])}
          />
        </div>
        <div className="column is-one-quarter date-selector-column">
          <div className="date-selector">
            <DatePickerInput
              mode="range"
              maxDate={buildingToday}
              timezone={buildingTimezone}
              placeholder={t('organisation:communicationPortal.navbar.dateRangePlaceholder')}
              size="small"
              value={period.length ? period : null}
              onRangeChange={setPeriod}
            />
          </div>
        </div>
        <div className="column is-half email-column">
          <AnalyticsCard
            icon={<EnvelopeSimple size={25} />}
            title={t('organisation:communicationPortal.analytics.emailTitle')}
            data={analytics.email ? analytics.email.subscribers : {}}
            trackingData={analytics.email ? analytics.email.tracking : {}}
            tooltipTexts={TOOLTIP_TEXTS.Email}
          />
        </div>
        <div className="column is-one-quarter notifications-column">
          <AnalyticsCard
            icon={<Bell size={25} />}
            title={t('organisation:communicationPortal.analytics.pushNotificationsTitle')}
            data={analytics.notification ? analytics.notification.subscribers : {}}
            tooltipTexts={TOOLTIP_TEXTS['Push notifications']}
          />
        </div>
        <div className="column is-one-quarter sms-column">
          {shouldShowActivateSmsPanel ? (
            <ActivateSmsCard />
          ) : (
            <AnalyticsCard
              icon={<ChatCircleText size={25} />}
              title={t('organisation:communicationPortal.analytics.smsTitle')}
              data={analytics.sms ? analytics.sms.subscribers : {}}
              tooltipTexts={TOOLTIP_TEXTS.SMS}
            />
          )}
        </div>
      </div>
    </div>
  )
}

AnnouncementsAnalytics.propTypes = {
  isOpen: PropTypes.bool,
  mailingListOptions: PropTypes.array,
  selectedBuilding: PropTypes.object,
  messageTypeCount: PropTypes.number,
}

export default AnnouncementsAnalytics
